import './index.scss'
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Modal, Tree, Checkbox, Input, Button, Switch, Tag } from 'antd'
import { useAppSelector, useAppDispatch } from '../../../app/hooks'
import { produce } from 'immer'
import classNames from 'classnames'
import { TodayIconFont as IconFont } from '../IconFonts'
import {
  findChildrenDepartmentIDs,
  findUserIDsInDepartment
} from '../../department/components'
import { Department, DepartmentMember, User } from '../../../global'
import { fetchGroupInfo } from '../../../actions/group'
import CONFIG from '../../../constants/config'
import sensors, { parsePlaceValue } from '../../../utils/sensors'
import { setPaymentVisible } from '../../../actions/main'
import crown_golden_img from '../../../images/crown_golden.png'
import { CaretDownOutlined, CloseOutlined } from '@ant-design/icons'

const Search = Input.Search

interface IModalFooterProps {
  onCancel: () => void
  onOk: () => void
  onlyInGroup?: boolean //仅显示当前在团队成员
}

export type SelectedDepartment = {
  id: string
  name: string
  num: number
}

export type FilterObj = {
  members: string[] //回显数据：已选普通成员
  userDepartmentIDs: string[] //回显数据：已选部门成员
  departments: SelectedDepartment[] //回显数据：已选部门
}

interface IDepartmentMemberPicker {
  open: boolean
  title?: string
  onCancel: () => void
  onOk: (params: {
    selectedNormalUserIDs: string[]
    selectedDepartmentUserIDs: string[]
    selectedDepartments: SelectedDepartment[]
  }) => void
  searchRef?: any
  filterObj: FilterObj
  vipRequired?: boolean //仅VIP可提交
  limitedTimeFree?: boolean //限时免费
  onlyInGroup?: boolean //仅显示当前在团队成员
}

const matchSourceDetail = () => {
  const pathname = window.location.pathname
  if (pathname === '/watermark/list') {
    return 'watermarkListChooseMember'
  } else if (pathname === '/watermark/editor') {
    return 'watermarkEditorChooseMember'
  } else {
    return pathname
  }
}

const ModalFooter: React.FC<IModalFooterProps> = (props) => {
  const { onCancel, onOk, onlyInGroup } = props

  const isOnlyInGroup = useAppSelector((state) => state.group.onlyInGroup) as
    | 0
    | 1
    | 2
  const groupID = useAppSelector((state) => state.group.groupId)
  const dispatch = useAppDispatch()
  const fetchGroupInfoAction = (data) => dispatch(fetchGroupInfo(data))

  const onIsOnlyInGroupChange = useCallback((checked: boolean) => {
    fetchGroupInfoAction({
      group_id: localStorage.getItem('group_id'),
      only_in_group: checked
        ? CONFIG.onlyInGroup.ONLY_IN_GROUP
        : CONFIG.onlyInGroup.ALL
    })
    sensors.track('web_workgroup_filter_member_page_click', {
      clickItem: checked ? 'onJobSwitch' : 'offJobSwitch',
      pagePath: window.location.pathname,
      groupID: groupID
    })
  }, [])

  return (
    <div className="modal-footer">
      {!onlyInGroup && (
        <div style={{ display: 'flex', flex: 1, alignItems: 'center' }}>
          <Switch
            size="small"
            checked={isOnlyInGroup === 2}
            onChange={onIsOnlyInGroupChange}
          />
          <span style={{ marginLeft: '10px' }}>仅显示当前在团队的成员</span>
        </div>
      )}
      <div>
        <Button onClick={onCancel}>取消</Button>
        <Button style={{ marginLeft: 8 }} type="primary" onClick={onOk}>
          确定
        </Button>
      </div>
    </div>
  )
}

const VipTip = ({ vipRequired, limitedTimeFree, modalOpen }) => {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((state) => state.group.groupId)
  const isVip = useAppSelector((state) => state.group.isVip)
  const onPay = () => dispatch(setPaymentVisible(true, matchSourceDetail()))
  const visible = useMemo(() => {
    return modalOpen && vipRequired && !limitedTimeFree && !isVip
  }, [modalOpen, vipRequired, limitedTimeFree, isVip])

  useEffect(() => {
    if (visible) {
      sensors.track('web_show_page_openapp_workgroupvip', {
        groupID: groupID,
        place: parsePlaceValue(),
        functionType: 'chooseMember'
      })
    }
  }, [visible])

  if (visible) {
    return (
      <div className="vip-tip">
        开通<strong>团队会员</strong>解锁使用
        <Button
          type="link"
          onClick={() => {
            sensors.track('web_click_button_workgroup_go_vip_trigger', {
              place: parsePlaceValue(),
              functionType: 'chooseMember',
              groupID
            })
            onPay()
          }}
        >
          <strong>[立即开通]</strong>
        </Button>
      </div>
    )
  }
  return null
}

const DepartmentUserPicker: React.FC<IDepartmentMemberPicker> = (props) => {
  const {
    title,
    open,
    onCancel,
    onOk,
    filterObj,
    vipRequired,
    limitedTimeFree,
    onlyInGroup
  } = props
  const organization: Department = useAppSelector(
    (state) => state.organization.organization
  )
  const normalUsers = useAppSelector((state) => state.group.members) // 现在 members 是普通数组，不再需要 toJS()
  const isOnlyInGroup = useAppSelector((state) => state.group.onlyInGroup) as
    | 0
    | 1
    | 2
  // const photoFilter = useAppSelector(state => state.photo.get('filter'))
  const groupID = useAppSelector((state) => state.group.groupId)
  const myRole = useAppSelector((state) => state.group.myRole)
  const members = useAppSelector((state) => state.group.members)
  const isVip = useAppSelector((state) => state.group.isVip)
  const dispatch = useAppDispatch()
  const onPay = () => dispatch(setPaymentVisible(true, matchSourceDetail()))

  //选择的部门列表，如果选择了父级部门，子级部门不能被选
  const [selectedDepartments, setSelectedDepartments] = useState<
    SelectedDepartment[]
  >([])
  //从组织架构中选择的成员列表
  const [selectedDepartmentUsers, setSelectedDepartmentUsers] = useState<
    DepartmentMember[]
  >([])
  //通过筛选成员列表选择的成员列表
  const [selectedNormalUsers, setSelectedNormalUsers] = useState<any[]>([])

  const [searchValue, setSearchValue] = useState<string>('')

  const [filtedNormalUsers, setFiltedNormalUsers] = useState<any[]>([])

  useEffect(() => {
    const filtedNormalUsers = normalUsers.filter(
      (user) => user.nickname.indexOf(searchValue.trim()) > -1
    )
    setFiltedNormalUsers(filtedNormalUsers)
  }, [searchValue, isOnlyInGroup])

  useEffect(() => {
    setSearchValue('')
  }, [open])

  useEffect(() => {
    //数据回显
    const { members, userDepartmentIDs, departments } = filterObj
    //从组织架构中选择或从搜索列表中选择，成员都是被选中，所以回显时，为了方便，统一赋值给已选普通成员列表
    const selectedNormalUsers = normalUsers.filter(
      (user) =>
        members.includes(user.user_id) ||
        userDepartmentIDs.includes(user.user_id)
    )
    setSelectedNormalUsers(selectedNormalUsers)
    setSelectedDepartmentUsers([])
    setSelectedDepartments(departments)
  }, [filterObj, open, isOnlyInGroup])

  const onCancelClick = useCallback(() => {
    onCancel()
    sensors.track('web_workgroup_filter_member_page_click', {
      clickItem: 'cancel',
      pagePath: window.location.pathname,
      groupID: groupID
    })
  }, [])

  const onOkClick = useCallback(() => {
    //将选择的部门列表（非ID列表）直接传递给Filter使用
    const selectedNormalUserIDs = selectedNormalUsers.map(
      (user) => user.user_id
    )
    const selectedDepartmentUserIDs = selectedDepartmentUsers.map(
      (user) => user.userID
    )

    const isWholeGroup =
      selectedDepartments.some((department) => department.id === '0') ||
      (selectedDepartments.length === 0 &&
        selectedNormalUserIDs.length === 0 &&
        selectedDepartmentUserIDs.length === 0)
    if (vipRequired && !limitedTimeFree && !isVip && !isWholeGroup) {
      sensors.track('web_click_button_workgroup_go_vip_trigger', {
        place: parsePlaceValue(),
        functionType: 'chooseMember',
        groupID
      })
      onPay()
      return
    }

    onOk({
      selectedNormalUserIDs,
      selectedDepartmentUserIDs,
      selectedDepartments
    })
    sensors.track('web_workgroup_filter_member_page_click', {
      clickItem: 'done',
      pagePath: window.location.pathname,
      groupID: groupID
    })

    const userNumInDepartments = selectedDepartments
      .map((department) => department.num)
      .reduce((acc, cur) => acc + cur, 0)
    const total =
      selectedNormalUserIDs.length +
      selectedDepartmentUserIDs.length +
      userNumInDepartments
    let onJobNum = 0,
      quitJobNum = 0
    if (
      total > 0 &&
      total !== members.length &&
      selectedDepartments.every((department) => department.id !== '0')
    ) {
      onJobNum =
        selectedNormalUsers.filter(
          (user) => user.user_status === CONFIG.memberStatus.NORMAL
        ).length +
        selectedDepartmentUsers.filter(
          (user) => user.userStatus === CONFIG.memberStatus.NORMAL
        ).length
      quitJobNum =
        selectedNormalUsers.filter(
          (user) => user.user_status === CONFIG.memberStatus.QUITED
        ).length +
        selectedDepartmentUsers.filter(
          (user) => user.userStatus === CONFIG.memberStatus.QUITED
        ).length
    }
    sensors.track('web_click_page_workgroup_filter_members', {
      onJobSwitch: isOnlyInGroup === 2 ? true : false,
      onJobNum,
      quitJobNum,
      place: parsePlaceValue(),
      role:
        myRole === 0
          ? 'member'
          : myRole === 2
            ? 'manager'
            : myRole === 1
              ? 'chiefManager'
              : '',
      groupID: groupID
    })
  }, [
    selectedNormalUsers,
    selectedDepartmentUsers,
    selectedDepartments,
    vipRequired,
    limitedTimeFree,
    isVip
  ])

  /**
   * 取消选择成员时，从2个已选成员列表中移除此成员
   * @param userID
   */
  const removeUserFromSeleteUsers = useCallback(
    (userID: string): void => {
      const selectedDepartmentUsersIndex = selectedDepartmentUsers.findIndex(
        (selectedDepartmentUser) => selectedDepartmentUser.userID === userID
      )
      const selectedNormalUsersIndex = selectedNormalUsers.findIndex(
        (selectedNormalUser) => selectedNormalUser.user_id === userID
      )

      if (selectedDepartmentUsersIndex > -1) {
        setSelectedDepartmentUsers(
          produce(selectedDepartmentUsers, (draft) => {
            draft.splice(selectedDepartmentUsersIndex, 1)
          })
        )
      }
      if (selectedNormalUsersIndex > -1) {
        const normalUsers = produce(selectedNormalUsers, (draft) => {
          draft.splice(selectedNormalUsersIndex, 1)
        })
        setSelectedNormalUsers(normalUsers)
      }
    },
    [selectedDepartmentUsers, selectedNormalUsers]
  )

  /**
   * 创建组织架构下的成员节点列表
   * @param users 要生成的成员节点
   * @param checkboxDisabled 如果父级部门已选中，则成员节点也选中，并且禁止操作
   * @returns 成员节点列表
   */
  const generateUserNodes = useCallback(
    (users: DepartmentMember[], checkboxDisabled: boolean) => {
      //仅显示在团队成员，权限最高，覆盖用户开关
      //是否展示已退出成员，普通用户是接口过滤返回，组织架构下用户需要前端过滤
      //过滤掉未激活的成员
      return users
        .filter((user) => {
          if (
            onlyInGroup ||
            isOnlyInGroup === CONFIG.onlyInGroup.ONLY_IN_GROUP
          ) {
            return user.userStatus === CONFIG.memberStatus.NORMAL
          } else {
            return user.userStatus !== CONFIG.memberStatus.INACTIVE
          }
        })
        .map((user) => {
          //从组织架构中选择或从搜索列表中选择，都是被选中
          const selected =
            selectedDepartmentUsers.some(
              (selectedUser) => selectedUser.userID === user.userID
            ) ||
            selectedNormalUsers.some(
              (normalUser) => normalUser.user_id === user.userID
            )
          const title = (
            <span className="treenode-title">
              <span className="name">
                {user.userStatus === CONFIG.memberStatus.QUITED && (
                  <Tag>已退出</Tag>
                )}
                {user.nickname}
              </span>
              <Checkbox
                checked={selected || checkboxDisabled} //如果父级部门已选中，则成员节点也选中，并且禁止操作
                disabled={checkboxDisabled} //如果父级部门已选中，则成员节点也选中，并且禁止操作
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedDepartmentUsers(
                      produce(selectedDepartmentUsers, (draft) => {
                        if (!selected) {
                          draft.push(user)
                        }
                      })
                    )
                    sensors.track('web_workgroup_filter_member_page_click', {
                      clickItem: 'selectOneMember',
                      memberStatus:
                        user.userStatus === CONFIG.memberStatus.QUITED
                          ? 'offJob'
                          : 'onJob',
                      pagePath: window.location.pathname,
                      groupID: groupID
                    })
                  } else {
                    removeUserFromSeleteUsers(user.userID)
                    sensors.track('web_workgroup_filter_member_page_click', {
                      clickItem: 'unSelectOneMember',
                      memberStatus:
                        user.userStatus === CONFIG.memberStatus.QUITED
                          ? 'offJob'
                          : 'onJob',
                      pagePath: window.location.pathname,
                      groupID: groupID
                    })
                  }
                }}
              />
            </span>
          )
          return {
            icon: <img className="headimg" src={user.headImgUrl} />,
            title,
            key: user.userID,
            isLeaf: true
          }
        })
    },
    [onlyInGroup, isOnlyInGroup, selectedDepartmentUsers, selectedNormalUsers]
  )

  /**
   *
   * @param users 普通成员列表
   * @returns 成员节点列表
   */
  const generateNormalUserNodes = useCallback(
    (users: User[]) => {
      return users
        .filter((user) => {
          //仅显示在团队成员，权限最高，覆盖用户开关
          if (onlyInGroup) {
            return user.user_status === CONFIG.memberStatus.NORMAL
          }
          return true
        })
        .map((user) => {
          //从组织架构中选择或从搜索列表中选择，都是被选中
          const selected =
            selectedDepartmentUsers.some(
              (selectedUser) => selectedUser.userID === user.user_id
            ) ||
            selectedNormalUsers.some(
              (normalUser) => normalUser.user_id === user.user_id
            )
          const title = (
            <span className="treenode-title">
              <span className="name">
                {user.user_status === CONFIG.memberStatus.QUITED && (
                  <Tag>已退出</Tag>
                )}
                {user.nickname}
              </span>
              <Checkbox
                checked={selected}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedNormalUsers(
                      produce(selectedNormalUsers, (draft) => {
                        if (!selected) {
                          draft.push(user)
                        }
                      })
                    )
                    sensors.track('web_workgroup_filter_member_page_click', {
                      clickItem: 'selectOneMember',
                      memberStatus:
                        user.user_status === CONFIG.memberStatus.QUITED
                          ? 'offJob'
                          : 'onJob',
                      pagePath: window.location.pathname,
                      groupID: groupID
                    })
                  } else {
                    removeUserFromSeleteUsers(user.user_id)
                    sensors.track('web_workgroup_filter_member_page_click', {
                      clickItem: 'unSelectOneMember',
                      memberStatus:
                        user.user_status === CONFIG.memberStatus.QUITED
                          ? 'offJob'
                          : 'onJob',
                      pagePath: window.location.pathname,
                      groupID: groupID
                    })
                  }
                }}
              />
            </span>
          )
          return {
            icon: <img className="headimg" src={user.headimgurl} />,
            title,
            key: user.user_id,
            isLeaf: true
          }
        })
    },
    [onlyInGroup, selectedDepartmentUsers, selectedNormalUsers]
  )

  /**
   *
   * @param departments 要生成的组织架构树
   * @param checkboxDisabled 如果父级已选中，则子节点也选中并禁止操作
   * @returns
   */
  const generateTreeNodes = useCallback(
    (departments: Department[], checkboxDisabled: boolean): any => {
      return departments.map((department) => {
        const selected = selectedDepartments.some(
          (selectedDepartment) => selectedDepartment.id === department.id
        )

        const title = (
          <span className="treenode-title">
            <span className="name">{department.name}</span>
            <Checkbox
              checked={selected || checkboxDisabled} //如果父级已选中，则子节点也选中并禁止操作
              disabled={checkboxDisabled} //如果父级已选中，则子节点也选中并禁止操作
              onChange={(e) => {
                if (e.target.checked) {
                  if (department.id === '0') {
                    setSelectedDepartments([
                      {
                        id: department.id,
                        name: department.name,
                        num: department.num
                      }
                    ])
                    setSelectedNormalUsers([])
                    setSelectedDepartmentUsers([])
                    sensors.track('web_workgroup_filter_member_page_click', {
                      clickItem: 'selectAll',
                      pagePath: window.location.pathname,
                      groupID: groupID
                    })
                  } else {
                    //选中部门
                    //检查已选择部门列表中是否包含子孙级节点，如果存在则过滤掉子孙级节点，只保留当前部门节点
                    setSelectedDepartments(
                      produce(selectedDepartments, (draft) => {
                        const departmentChildIDs =
                          findChildrenDepartmentIDs(department)
                        const mDraft = draft.filter(
                          (selectedDepartment) =>
                            !departmentChildIDs.includes(selectedDepartment.id)
                        )
                        if (!selected) {
                          mDraft.push({
                            id: department.id,
                            name: department.name,
                            num: department.num
                          })
                        }
                        return mDraft
                      })
                    )
                    //检查已选择部门成员列表中是否包含此部门下的成员，如果存在则过滤掉，只保留当前部门节点
                    setSelectedDepartmentUsers(
                      produce(selectedDepartmentUsers, (draft) => {
                        const userIDs = findUserIDsInDepartment(department)
                        const mDraft = draft.filter(
                          (selectedDepartmentUser) =>
                            !userIDs.includes(selectedDepartmentUser.userID)
                        )
                        return mDraft
                      })
                    )
                  }
                  sensors.track('web_workgroup_filter_member_page_click', {
                    clickItem: 'selectOneDepartment',
                    pagePath: window.location.pathname,
                    groupID: groupID
                  })
                } else {
                  setSelectedDepartments(
                    produce(selectedDepartments, (draft) => {
                      draft.splice(
                        draft.findIndex(
                          (selectedDepartment) =>
                            selectedDepartment.id === department.id
                        ),
                        1
                      )
                    })
                  )
                  sensors.track('web_workgroup_filter_member_page_click', {
                    clickItem: 'unSelectOneDepartment',
                    pagePath: window.location.pathname,
                    groupID: groupID
                  })
                  if (department.id === '0') {
                    sensors.track('web_workgroup_filter_member_page_click', {
                      clickItem: 'unSelectAll',
                      pagePath: window.location.pathname,
                      groupID: groupID
                    })
                  }
                }
              }}
            />
          </span>
        )
        const childNodes: any[] = []
        if (department.children.length > 0) {
          childNodes.push(
            ...generateTreeNodes(
              department.children,
              selected || checkboxDisabled
            )
          )
        }
        if (department.users.length > 0) {
          childNodes.push(
            ...generateUserNodes(department.users, selected || checkboxDisabled)
          )
        }
        return {
          icon: <IconFont type="icondepartment" style={{ color: '#0093FF' }} />,
          title,
          key: department.id,
          children: childNodes.length > 0 ? childNodes : undefined
        }
      })
    },
    [selectedDepartments, selectedDepartmentUsers, generateUserNodes]
  )

  const renderSelectedDepartments = useCallback(() => {
    const departments = selectedDepartments.map((department) => {
      return (
        <div className="selected-department" key={department.id}>
          <div className="department-name">
            <IconFont type="icondepartment" style={{ color: '#0093FF' }} />
            <span className="name">{department.name}</span>
          </div>
          <CloseOutlined
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setSelectedDepartments(
                produce(selectedDepartments, (draft) => {
                  draft.splice(
                    draft.findIndex((item) => item.id === department.id),
                    1
                  )
                })
              )
              sensors.track('web_workgroup_filter_member_page_click', {
                clickItem: 'cancelOneDepartment',
                pagePath: window.location.pathname,
                groupID: groupID
              })
            }}
          />
        </div>
      )
    })

    const users = selectedDepartmentUsers.map((user) => {
      return (
        <div className="selected-department" key={user.userID}>
          <div className="user-name">
            <img className="headimg" src={user.headImgUrl} />
            <span className="name">
              {user.userStatus === CONFIG.memberStatus.QUITED && (
                <Tag>已退出</Tag>
              )}
              {user.nickname}
            </span>
          </div>
          <CloseOutlined
            style={{ cursor: 'pointer' }}
            onClick={() => {
              removeUserFromSeleteUsers(user.userID)
              sensors.track('web_workgroup_filter_member_page_click', {
                clickItem: 'cancelOneMember',
                memberStatus:
                  user.userStatus === CONFIG.memberStatus.QUITED
                    ? 'offJob'
                    : 'onJob',
                pagePath: window.location.pathname,
                groupID: groupID
              })
            }}
          />
        </div>
      )
    })

    const normalUsers = selectedNormalUsers.map((user) => {
      return (
        <div className="selected-department" key={user.user_id}>
          <div className="user-name">
            <img className="headimg" src={user.headimgurl} />
            <span className="name">
              {user.user_status === CONFIG.memberStatus.QUITED && (
                <Tag>已退出</Tag>
              )}
              {user.nickname}
            </span>
          </div>
          <CloseOutlined
            style={{ cursor: 'pointer' }}
            onClick={() => {
              removeUserFromSeleteUsers(user.user_id)
              sensors.track('web_workgroup_filter_member_page_click', {
                clickItem: 'cancelOneMember',
                memberStatus:
                  user.user_status === CONFIG.memberStatus.QUITED
                    ? 'offJob'
                    : 'onJob',
                pagePath: window.location.pathname,
                groupID: groupID
              })
            }}
          />
        </div>
      )
    })

    return (
      <>
        {departments}
        {users}
        {normalUsers}
      </>
    )
  }, [selectedDepartmentUsers, selectedDepartments, selectedNormalUsers])

  return (
    <Modal
      wrapClassName={classNames('department-members-picker-modal', {
        'vip-required': vipRequired && !limitedTimeFree && !isVip
      })}
      centered
      open={open}
      getContainer={false}
      title={
        <>
          {title || '筛选成员'}
          {limitedTimeFree && (
            <span className="limited-time-free">免费体验</span>
          )}
          {vipRequired && (
            <img className="title-vip-icon" src={crown_golden_img} />
          )}
        </>
      }
      width={600}
      styles={{ body: { width: '600px', height: '450px' } }}
      onCancel={onCancelClick}
      footer={
        <ModalFooter
          onCancel={onCancelClick}
          onOk={onOkClick}
          onlyInGroup={onlyInGroup}
        />
      }
    >
      <VipTip
        vipRequired={vipRequired}
        limitedTimeFree={limitedTimeFree}
        modalOpen={open}
      />
      <div className="modal-content ">
        <div className="source">
          <h3>选择</h3>
          <div className="department-container">
            <div className="search-warp">
              <Search
                size="small"
                allowClear
                enterButton
                placeholder="请输入姓名搜索"
                value={searchValue}
                style={{ width: '228px', margin: '10px' }}
                onChange={(e) => setSearchValue(e.target.value)}
                onSearch={(value) => {
                  setSearchValue(value)
                  sensors.track('web_workgroup_filter_member_page_click', {
                    clickItem: 'searchMember',
                    pagePath: window.location.pathname,
                    groupID: groupID
                  })
                }}
              />
            </div>
            <div className="tree-wrap">
              {searchValue.trim() && (
                <Tree
                  showIcon
                  blockNode
                  multiple={false}
                  selectable={false}
                  treeData={generateNormalUserNodes(filtedNormalUsers)}
                />
              )}

              {
                //为了让defaultExpandedKeys生效，需要有数据后再渲染树，否则树从无到有进行更新，defaultExpandedKeys将不生效
                !searchValue.trim() && organization && (
                  <Tree
                    showIcon
                    blockNode
                    multiple={false}
                    selectable={false}
                    defaultExpandedKeys={['0']}
                    switcherIcon={
                      <CaretDownOutlined style={{ fontSize: '14px' }} />
                    }
                    treeData={generateTreeNodes([organization], false)}
                  />
                )
              }
            </div>
          </div>
        </div>
        <div className="target">
          <h3>已选</h3>
          <div className="department-container">
            {renderSelectedDepartments()}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default DepartmentUserPicker
